import type { CharacterPose } from '../../content/schema/character';
import type {
  MapObjectInstance,
  MapLight,
  MapTriggerVolume,
  MapDecal,
  MapRoomMarker,
} from '../../content/schema/map';

/**
 * Transport-neutral persistence contracts for the editor and Character Studio.
 *
 * The engine never knows *how* a project is stored - a game's dev-server, a
 * Firestore document, or an in-memory fixture all implement these interfaces
 * and are injected at startup via `setMapPersistence` / `setPosePersistence`.
 * The map editor store and Character Studio resolve the active implementation
 * lazily through `getMapPersistence()` / `getPosePersistence()`, scoped to the
 * project named by `setActiveProjectId`.
 */

export interface SaveOptions {
  /** Write to a transient autosave slot rather than the canonical source. */
  autosave?: boolean;
  /** Persist even if schema validation produced blocking errors. */
  skipValidation?: boolean;
  /** Fired during page teardown (pagehide/beforeunload): the transport must
   *  outlive the document, so the request goes out with fetch's keepalive
   *  flag instead of the caller's normal AbortController timeout. */
  keepalive?: boolean;
}

export interface MapPersistenceResult {
  objectInstances: MapObjectInstance[];
  lights?: MapLight[];
  triggers?: MapTriggerVolume[];
  decals?: MapDecal[];
  markers?: MapRoomMarker[];
}

export interface SnapshotResult {
  snapshotId: string;
  label?: string;
}

export interface MapPersistence {
  /** Load the canonical map document for a project. */
  loadMap(projectId: string): Promise<MapPersistenceResult>;
  /** Persist the working set; `options.autosave` targets the transient slot. */
  saveMap(
    projectId: string,
    instances: MapObjectInstance[],
    options?: SaveOptions,
    nonProps?: {
      lights?: MapLight[];
      triggers?: MapTriggerVolume[];
      decals?: MapDecal[];
      markers?: MapRoomMarker[];
    },
  ): Promise<MapPersistenceResult>;
  /**
   * Load a versioned snapshot. With no `snapshotId` the implementation returns
   * its canonical baseline (git HEAD for the dev-server, the latest snapshot
   * for Firestore) - the source of the pre-save diff.
   */
  loadSnapshot(projectId: string, snapshotId?: string): Promise<MapPersistenceResult>;
  /** Capture a labelled snapshot of the given instances. */
  createSnapshot(
    projectId: string,
    instances: MapObjectInstance[],
    label: string,
    nonProps?: {
      lights?: MapLight[];
      triggers?: MapTriggerVolume[];
      decals?: MapDecal[];
      markers?: MapRoomMarker[];
    },
  ): Promise<SnapshotResult>;
}

export interface PosePersistenceResult {
  poses: CharacterPose[];
}

export interface PosePersistence {
  loadPoses(projectId: string): Promise<PosePersistenceResult>;
  savePoses(projectId: string, poses: CharacterPose[]): Promise<PosePersistenceResult>;
}

// ── Module-level injection ──────────────────────────────────────────────────────

let activeMapPersistence: MapPersistence | null = null;
let activePosePersistence: PosePersistence | null = null;
let activeProjectId: string | null = null;

/** Install the map persistence implementation. Pass `null` to clear. */
export function setMapPersistence(impl: MapPersistence | null): void {
  activeMapPersistence = impl;
}

/** Install the pose persistence implementation. Pass `null` to clear. */
export function setPosePersistence(impl: PosePersistence | null): void {
  activePosePersistence = impl;
}

/** Set the project all subsequent persistence operations target. */
export function setActiveProjectId(projectId: string | null): void {
  activeProjectId = projectId;
}

export function getActiveProjectId(): string {
  if (!activeProjectId) {
    throw new Error(
      'No active project. Call setActiveProjectId(projectId) before persistence operations.',
    );
  }
  return activeProjectId;
}

export function getMapPersistence(): MapPersistence {
  if (!activeMapPersistence) {
    throw new Error(
      'Map persistence is not configured. Call setMapPersistence(impl) at startup.',
    );
  }
  return activeMapPersistence;
}

export function getPosePersistence(): PosePersistence {
  if (!activePosePersistence) {
    throw new Error(
      'Pose persistence is not configured. Call setPosePersistence(impl) at startup.',
    );
  }
  return activePosePersistence;
}
